import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import Image from 'gatsby-plugin-sanity-image';
import PortableText from './portable-text';
import Close from '../svg/close.svg';
import CardScroller, { cardStyles } from './card-scroller';
import useAnimatedStatic from './use-animated-static';
import { hasContent, trimTrailingBlankBlocks } from '../content';

const control = css`
  color: var(--black);
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  min-height: 44px;
  min-width: 44px;
  transition: color 0.6s var(--curve);
  &:hover {
    color: var(--primary);
    transition: color 0s;
  }
  &:focus-visible {
    outline: 2px solid var(--black);
    outline-offset: 4px;
  }
  &:disabled {
    color: var(--gray);
    cursor: default;
  }
`;

function captionText(item) {
  const caption = item._rawCaption || item.caption;
  if (typeof caption === 'string') return caption;
  return (caption || [])
    .filter(block => block._type === 'block')
    .map(block => block.children?.map(span => span.text || '').join(''))
    .join(' ');
}

function Caption({ item }) {
  const caption = item._rawCaption || item.caption;
  if (!hasContent(caption)) return null;
  return (
    <div
      className="type--20"
      css={css`
        white-space: pre-line;
        overflow-wrap: anywhere;
        p {
          margin-bottom: 0;
        }
      `}
    >
      {typeof caption === 'string' ? (
        caption
      ) : (
        <PortableText blocks={trimTrailingBlankBlocks(caption)} />
      )}
    </div>
  );
}

function HistoryDialog({ item, introduction, onClose }) {
  const dialog = useRef(null);
  const backgroundPosition = useAnimatedStatic(true);
  const content = trimTrailingBlankBlocks(
    hasContent(item._rawContent)
      ? item._rawContent
      : hasContent(item.content)
      ? item.content
      : introduction
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current.showModal();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <dialog
      ref={dialog}
      aria-label={
        item.title || captionText(item) || 'Historical image and information'
      }
      style={{ backgroundPosition }}
      onClose={onClose}
      onKeyDown={event => {
        if (event.key !== 'Tab') return;
        const focusable = [
          ...dialog.current.querySelectorAll(
            'button, a[href], input, select, textarea, [tabindex]'
          ),
        ].filter(
          element =>
            !element.disabled &&
            element.tabIndex >= 0 &&
            element.getClientRects().length > 0
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }}
      onClick={event => {
        if (event.target === dialog.current) dialog.current.close();
      }}
      css={css`
        position: fixed;
        inset: 0;
        width: 100%;
        max-width: none;
        height: 100%;
        height: 100dvh;
        max-height: none;
        margin: 0;
        padding: 0;
        border: 0;
        background-image: url('/images/background-static.png');
        color: var(--black);
        z-index: 100;
        &::backdrop {
          background: var(--black);
        }
      `}
    >
      <button
        type="button"
        aria-label="Close historical image"
        onClick={() => dialog.current.close()}
        css={[
          control,
          css`
            position: fixed;
            top: var(--gutter);
            right: calc(var(--bigMargin) + var(--gutter));
            z-index: 1;
            display: grid;
            place-items: center;
            background: var(--white);
            border-radius: 50%;
            svg {
              display: block;
              width: 6rem;
              height: auto;
              overflow: visible;
            }
            circle,
            path {
              stroke: currentColor;
            }
            @media (max-width: 700px) {
              right: calc(var(--gutter) * 2);
            }
          `,
        ]}
      >
        <Close />
      </button>
      <div
        className="type--45"
        css={css`
          position: relative;
          min-height: 100%;
          margin: 0 var(--bigMargin);
          padding: var(--margin);
          padding-top: calc(var(--gutter) * 2 + max(6rem, 44px));
          background-color: var(--white);
          @media (max-width: 700px) {
            margin: 0 var(--gutter);
          }
        `}
      >
        <Image
          {...item.image}
          alt={
            item.altText ||
            captionText(item) ||
            item.image.asset?.description ||
            'Historical photograph of The Substation'
          }
          width={1980}
          loading="eager"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            marginBottom: hasContent(item._rawCaption || item.caption)
              ? 'var(--gutter)'
              : 0,
          }}
        />
        <Caption item={item} />
        {content?.length > 0 && (
          <div
            css={css`
              margin-top: var(--margin);
            `}
          >
            <PortableText blocks={content} />
          </div>
        )}
      </div>
    </dialog>,
    document.body
  );
}

export default function HistoryGallery({ images = [], introduction }) {
  const scroller = useRef(null);
  const [selected, setSelected] = useState(null);
  const availableImages = images.filter(item => item.image?.asset);

  if (!availableImages.length) return null;

  const scroll = direction =>
    scroller.current.scrollBy({
      left: direction * scroller.current.clientWidth * 0.8,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });

  return (
    <div
      css={css`
        margin: var(--margin) calc(var(--margin) * -1) 0;
      `}
    >
      <CardScroller
        ref={scroller}
        role="region"
        aria-label="History images"
        tabIndex={0}
        onKeyDown={event => {
          if (
            event.target === scroller.current &&
            ['ArrowLeft', 'ArrowRight'].includes(event.key)
          ) {
            event.preventDefault();
            scroll(event.key === 'ArrowLeft' ? -1 : 1);
          }
        }}
        css={css`
          padding-bottom: var(--gutter);
          margin-bottom: 0;
          &:focus-visible {
            outline: 2px solid var(--black);
            outline-offset: 4px;
          }
        `}
      >
        {availableImages.map((item, index) => (
          <figure key={item._key || index} css={cardStyles}>
            <button
              type="button"
              aria-label={`Learn more about ${
                item.title || captionText(item) || `history image ${index + 1}`
              }`}
              css={[
                control,
                css`
                  display: block;
                  width: 100%;
                  &:hover .history-title,
                  &:focus-visible .history-title {
                    color: var(--primary);
                  }
                `,
              ]}
              onClick={() => setSelected(item)}
            >
              <Image
                {...item.image}
                alt={
                  item.altText ||
                  captionText(item) ||
                  item.image.asset?.description ||
                  'Historical photograph of The Substation'
                }
                width={990}
                height={620}
                loading="eager"
                style={{
                  display: 'block',
                  width: '100%',
                  marginBottom: hasContent(item.title) ? '0.5em' : 0,
                }}
              />
              {hasContent(item.title) && (
                <div
                  className="history-title"
                  css={css`
                    text-transform: uppercase;
                    overflow-wrap: anywhere;
                  `}
                >
                  {item.title}
                </div>
              )}
            </button>
            <figcaption>
              <Caption item={item} />
            </figcaption>
          </figure>
        ))}
      </CardScroller>
      {selected && (
        <HistoryDialog
          item={selected}
          introduction={introduction}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
